import React, { useMemo, useState } from "react";
import ExcelGrid from "components/ExcelGrid";
import SectionCard from "components/SectionCard";
import { DropdownSelect, FileBrowseInput } from "components/InputStyled";
import { useDialog } from "context/DialogContext";
import { logAudit } from "lib/audit";
import { extractBnyPershingGcusZip } from "lib/bnyPershing/gcusArchive";
import { extractBnyPershingIscaZip, sha256Hex } from "lib/bnyPershing/iscaArchive";
import {
  classifyPershingDailyFiles,
  extractPershingDailyTextZip,
  PERSHING_DAILY_FILE_TYPES,
  REQUIRED_PERSHING_DAILY_FILE_TYPES,
} from "lib/bnyPershing/dailyPackage";
import { supabase } from "services/supabaseClient";

const CUSTODIANS = [{ value: "bny_pershing", label: "BNY Pershing" }];
const DATA_TYPES = [{ value: "daily_package", label: "Daily Package" }];

const auditSummary = (data = {}) => ({
  businessDate: data.business_date, gcusHoldings: data.gcus_holdings,
  readyHoldings: data.ready_holdings, positionValuations: data.position_valuations_ready,
  unmappedAccounts: data.unmapped_accounts, noMatchingPositions: data.no_matching_positions,
  quantityMismatches: data.quantity_mismatches, marketValueMismatches: data.market_value_mismatches,
});

export default function CustodianDataUploader() {
  const { confirm } = useDialog();
  const [custodian, setCustodian] = useState("bny_pershing");
  const [dataType, setDataType] = useState("daily_package");
  const [dailyFiles, setDailyFiles] = useState([]);
  const [localSummary, setLocalSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prepared, setPrepared] = useState(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const columns = useMemo(() => [
    { key: "source_accounts", label: "GCUS Account", mono: true, width: "w-36" },
    { key: "account_key", label: "Account Key", mono: true, width: "w-32" },
    { key: "mapping_external_id", label: "Mapped External ID", mono: true, width: "w-36" },
    { key: "org_name", label: "Org", width: "w-48" },
    { key: "fund_name", label: "Fund", width: "w-48" },
    { key: "cusip", label: "CUSIP / ID", mono: true, width: "w-32" },
    { key: "valuation_date", label: "Valuation Date", date: true, mono: true, width: "w-32" },
    { key: "isca_price", label: "ISCA Price", numeric: true, mono: true, width: "w-28",
      format: (value) => value == null ? "" : Number(value).toFixed(6) },
    { key: "gcus_trade_quantity", label: "GCUS Trade Qty", numeric: true, mono: true, width: "w-32" },
    { key: "gcus_settled_quantity", label: "GCUS Settled Qty", numeric: true, mono: true, width: "w-32" },
    { key: "position_quantity", label: "Position Qty", numeric: true, mono: true, width: "w-32" },
    { key: "holding_status", label: "Reconciliation", width: "w-40" },
    { key: "valuation_action", label: "Action", width: "w-28" },
    { key: "existing_price", label: "Existing Price", numeric: true, mono: true, width: "w-28",
      format: (value) => value == null ? "" : Number(value).toFixed(6) },
  ], []);

  const reset = () => { setLocalSummary(null); setPreview(null); setPrepared(null); setStatus(""); };

  const classifiedPackage = useMemo(() => {
    try { return classifyPershingDailyFiles(dailyFiles); } catch { return { date: null, files: {} }; }
  }, [dailyFiles]);

  const selectDailyFiles = (files) => {
    reset();
    try {
      const classified = classifyPershingDailyFiles(files);
      setDailyFiles(files);
      const missing = REQUIRED_PERSHING_DAILY_FILE_TYPES.filter((type) => !classified.files[type]);
      setStatus(missing.length
        ? `Selected package is missing required file${missing.length === 1 ? "" : "s"}: ${missing.join(", ")}.`
        : `Daily package ${classified.date} is ready to validate.`);
    } catch (error) {
      setDailyFiles(files);
      setStatus(`Package selection failed: ${error.message}`);
    }
  };

  const prepareFiles = async () => {
    const packageInfo = classifyPershingDailyFiles(dailyFiles);
    const missing = REQUIRED_PERSHING_DAILY_FILE_TYPES.filter((type) => !packageInfo.files[type]);
    if (missing.length) throw new Error(`Select the required BNY Pershing files: ${missing.join(", ")}.`);
    const selectedEntries = Object.values(packageInfo.files);
    const byteEntries = await Promise.all(selectedEntries.map(async (entry) => [
      entry.type, await entry.file.arrayBuffer().then((value) => new Uint8Array(value)),
    ]));
    const bytesByType = Object.fromEntries(byteEntries);
    const gcusBytes = bytesByType.GCUS;
    const iscaBytes = bytesByType.ISCA;
    const [gcus, isca] = [
      extractBnyPershingGcusZip(gcusBytes), extractBnyPershingIscaZip(iscaBytes),
    ];
    if (gcus.parsed.file_date !== isca.parsed.file_date) {
      throw new Error(`GCUS date ${gcus.parsed.file_date || "unknown"} does not match ISCA date ${isca.parsed.file_date || "unknown"}.`);
    }
    if (gcus.parsed.file_date !== packageInfo.date) {
      throw new Error(`Filename date ${packageInfo.date} does not match the Pershing data date ${gcus.parsed.file_date || "unknown"}.`);
    }
    const staged = {};
    for (const type of ["GTDE", "GSDE"]) {
      if (!packageInfo.files[type]) continue;
      staged[type] = extractPershingDailyTextZip(
        bytesByType[type], type, packageInfo.files[type].stem
      );
    }
    setLocalSummary({ gcusMember: gcus.memberName, iscaMember: isca.memberName,
      fileDate: gcus.parsed.file_date, holdings: gcus.parsed.rows.length,
      securities: isca.parsed.rows.length,
      gtdeMember: staged.GTDE?.memberName, gtdeTransactions: staged.GTDE?.contents.split(/\r?\n/).filter((line) => line.startsWith("GEA")).length || 0,
      gsdeMember: staged.GSDE?.memberName, gsdeTransactions: staged.GSDE?.contents.split(/\r?\n/).filter((line) => line.startsWith("GSA")).length || 0,
      rejected: gcus.parsed.rejected.length + isca.parsed.rejected.length,
      errors: gcus.parsed.errors.length + isca.parsed.errors.length });
    const issue = gcus.parsed.errors[0]?.code || gcus.parsed.rejected[0]?.reasons?.join(", ")
      || isca.parsed.errors[0]?.code || isca.parsed.rejected[0]?.reasons?.join(", ");
    if (issue) throw new Error(`Custodian file validation failed (${issue}).`);
    const [gcusHash, iscaHash] = await Promise.all([sha256Hex(gcusBytes), sha256Hex(iscaBytes)]);
    return { fileDate: gcus.parsed.file_date,
      gcus: { fileName: packageInfo.files.GCUS.file.name, hash: gcusHash, rows: gcus.parsed.rows },
      isca: { fileName: packageInfo.files.ISCA.file.name, hash: iscaHash, rows: isca.parsed.rows },
      stagedTypes: Object.keys(staged) };
  };

  const callImport = async (files, dryRun) => {
    const result = await supabase.rpc("bny_pershing_import_position_valuations", {
      p_gcus_file_name: files.gcus.fileName, p_gcus_file_date: files.fileDate,
      p_gcus_file_sha256: files.gcus.hash, p_gcus_rows: files.gcus.rows,
      p_isca_file_name: files.isca.fileName, p_isca_file_date: files.fileDate,
      p_isca_file_sha256: files.isca.hash, p_isca_rows: files.isca.rows,
      p_dry_run: dryRun,
    });
    if (result.error || !result.data?.rows?.length) return result;

    const fundIds = [...new Set(result.data.rows.map((row) => row.fund_id).filter(Boolean))];
    if (!fundIds.length) return result;
    const { data: fundRows, error: fundError } = await supabase
      .from("fp_investment_funds")
      .select("id, org_id")
      .in("id", fundIds);
    if (fundError) return result;
    const orgIds = [...new Set((fundRows || []).map((fund) => fund.org_id).filter(Boolean))];
    const { data: orgRows, error: orgError } = orgIds.length
      ? await supabase.from("orgs").select("id, name").in("id", orgIds)
      : { data: [], error: null };
    if (orgError) return result;
    const orgById = new Map((orgRows || []).map((org) => [org.id, org.name]));
    const orgByFundId = new Map((fundRows || []).map((fund) => [fund.id, orgById.get(fund.org_id) || ""]));
    return {
      ...result,
      data: { ...result.data, rows: result.data.rows.map((row) => ({
        ...row, org_name: orgByFundId.get(row.fund_id) || "",
      })) },
    };
  };

  const previewImport = async () => {
    setBusy(true); setPreview(null); setPrepared(null);
    setStatus("Opening, classifying, and validating the BNY Pershing daily package…");
    try {
      const files = await prepareFiles();
      setStatus("Resolving Pershing accounts and reconciling FundPlanner positions…");
      const { data, error } = await callImport(files, true);
      if (error) throw new Error(error.message);
      setPrepared(files); setPreview(data);
      setStatus(`Daily package preview complete. Valuations are ready for review.${files.stagedTypes.length ? ` ${files.stagedTypes.join(" and ")} validated and staged for upcoming processing.` : ""} No database records were changed.`);
      await logAudit({ category: "upload", action: "bny_pershing_valuation_preview", success: true,
        detail: { gcusFile: files.gcus.fileName, iscaFile: files.isca.fileName, ...auditSummary(data) } });
    } catch (error) {
      setStatus(`Preview failed: ${error.message}`);
      await logAudit({ category: "upload", action: "bny_pershing_valuation_preview", success: false,
        detail: { files: dailyFiles.map((file) => file.name), error: error.message } });
    } finally { setBusy(false); }
  };

  const commitImport = async () => {
    if (!prepared || !preview) return;
    const ok = await confirm(
      `Import ${preview.position_valuations_ready || 0} reconciled BNY Pershing position valuation(s) dated ${prepared.fileDate}?`,
      { title: "Import Custodian Valuations", okLabel: "Import", cancelLabel: "Cancel" });
    if (!ok) return;
    setBusy(true); setStatus("Importing reconciled BNY Pershing position valuations…");
    try {
      const { data, error } = await callImport(prepared, false);
      if (error) throw new Error(error.message);
      setPreview(data); setPrepared(null);
      setStatus(data.status === "already_processed"
        ? `These files were already processed in batch ${data.batch_id}. No duplicates were created.`
        : `Import complete. Batch ${data.batch_id}.`);
      await logAudit({ category: "upload", action: "bny_pershing_valuation_import", success: true,
        detail: { gcusFile: prepared.gcus.fileName, iscaFile: prepared.isca.fileName,
          batchId: data.batch_id, status: data.status, ...auditSummary(data) } });
    } catch (error) {
      setStatus(`Import failed: ${error.message}`);
      await logAudit({ category: "upload", action: "bny_pershing_valuation_import", success: false,
        detail: { files: dailyFiles.map((file) => file.name), error: error.message } });
    } finally { setBusy(false); }
  };

  const summary = preview ? [
    ["GCUS holdings", preview.gcus_holdings], ["Ready holdings", preview.ready_holdings],
    ["Position valuations", preview.position_valuations_ready], ["Unmapped accounts", preview.unmapped_accounts],
    ["No position", preview.no_matching_positions], ["Qty mismatch", preview.quantity_mismatches],
    ["Value mismatch", preview.market_value_mismatches], ["Cash excluded", preview.excluded_cash_holdings],
  ] : [];

  return <SectionCard title="Upload Custodian Data">
    <p className="mb-4 text-sm text-muted">Select the BNY Pershing daily ZIP files together. Files are classified by their MMDDYY.TYPE.ZIP names. GCUS, ISCA, and GTDE are required; GSDE may also be included. The current import updates only reconciled position prices.</p>
    <div className="flex flex-wrap items-end gap-3">
      <DropdownSelect label="Custodian" value={custodian} onChange={(v) => { setCustodian(v); reset(); }} options={CUSTODIANS} className="min-w-56" />
      <DropdownSelect label="Data Type" value={dataType} onChange={(v) => { setDataType(v); reset(); }} options={DATA_TYPES} className="min-w-80" />
      <FileBrowseInput label="Daily Package ZIP Files" file={dailyFiles} multiple accept=".zip,application/zip" placeholder="Select GCUS, ISCA, GTDE, and optional GSDE"
        browseLabel="Select files…" onFileChange={selectDailyFiles} inputKey={`${custodian}-${dataType}-package`} className="min-w-[28rem]" />
    </div>
    {dailyFiles.length ? <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {PERSHING_DAILY_FILE_TYPES.map((type) => {
        const entry = classifiedPackage.files[type];
        const required = REQUIRED_PERSHING_DAILY_FILE_TYPES.includes(type);
        return <div key={type} className={`rounded-md border px-3 py-2 ${entry ? "border-border" : required ? "border-red-300" : "border-border"}`}>
          <div className="text-xs font-semibold text-app">{type}{required ? " · Required" : " · Optional"}</div>
          <div className="truncate text-xs text-muted">{entry?.file.name || "Not selected"}</div>
        </div>;
      })}
    </div> : null}
    <div className="mt-4 flex gap-3">
      <button type="button" onClick={previewImport} disabled={REQUIRED_PERSHING_DAILY_FILE_TYPES.some((type) => !classifiedPackage.files[type]) || busy} className="btn btn-primary disabled:opacity-50">{busy && !preview ? "Preparing…" : "Preview Daily Package"}</button>
      {prepared && preview?.dry_run ? <button type="button" onClick={commitImport} disabled={busy} className="btn btn-primary disabled:opacity-50">Import Valuations</button> : null}
    </div>
    {status ? <p className="mt-3 whitespace-pre-line text-sm">{status}</p> : null}
    {localSummary ? <p className="mt-3 text-xs text-muted">GCUS: {localSummary.gcusMember} · ISCA: {localSummary.iscaMember} · GTDE: {localSummary.gtdeMember} ({localSummary.gtdeTransactions} transactions){localSummary.gsdeMember ? ` · GSDE: ${localSummary.gsdeMember} (${localSummary.gsdeTransactions} transactions)` : ""} · Date: {localSummary.fileDate} · {localSummary.holdings} holdings · {localSummary.securities} prices · {localSummary.rejected} rejected</p> : null}
    {preview ? <div className="mt-5 border-t border-border pt-5">
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-8">{summary.map(([label, value]) => <div key={label} className="rounded-md border border-border px-3 py-2"><div className="text-xs text-muted">{label}</div><div className="text-lg font-semibold text-app">{value ?? 0}</div></div>)}</div>
      <ExcelGrid columns={columns} rows={preview.rows || []} showCopyButton={true} />
    </div> : null}
  </SectionCard>;
}
